import type { CarePlan, Condition, Goal, QuestionnaireResponse } from '@medplum/fhirtypes';
import { parseTreatmentPlanDraft } from '@unify/fhir-model/resources/treatment-plan';
import type {
  ParsedTreatmentPlanDiagnosis,
  ParsedTreatmentPlanDraft,
} from '@unify/fhir-model/resources/treatment-plan';
import { parseTreatmentPlanFromCarePlan } from '@unify/fhir-model/resources/treatment-plan-careplan';
import type {
  ObjectiveBlock,
  TreatmentGoal,
  TreatmentPlan,
  TreatmentPlanDiagnosisSection,
} from '../pages/clients/data/types';
import {
  collectAnswerStringsByLinkIdSuffix,
  extractDiagnosesFromQuestionnaireResponse,
} from './treatmentPlanDiagnosis';

function findFirstAnswerByLinkIdSuffix(
  items: QuestionnaireResponse['item'],
  suffix: string,
): string | undefined {
  let found: string | undefined;
  const walk = (nodes: QuestionnaireResponse['item']): void => {
    if (found) return;
    for (const node of nodes ?? []) {
      if (node.linkId?.endsWith(suffix)) {
        const value = node.answer?.[0]?.valueString?.trim();
        if (value) {
          found = value;
          return;
        }
      }
      walk(node.item);
    }
  };
  walk(items);
  return found;
}

function splitDiagnosis(raw: string | undefined): {
  diagnosisCode?: string;
  diagnosisDescription?: string;
} {
  if (!raw) return {};
  const [code, ...description] = raw.split(/\s*[-–—]\s*/);
  return {
    diagnosisCode: code?.trim() || undefined,
    diagnosisDescription: description.join(' - ').trim() || undefined,
  };
}

function treatmentGoals(
  diagnosis: ParsedTreatmentPlanDiagnosis,
  idPrefix: string,
): TreatmentGoal[] {
  return diagnosis.goals
    .filter((goal) => goal.text)
    .map((goal): TreatmentGoal => {
      const goalId = `${idPrefix}-d${diagnosis.responseIndex}-g${goal.responseIndex}`;
      const objectiveBlocks: ObjectiveBlock[] = goal.objectives
        .filter((objective) => objective.text || objective.interventions)
        .map((objective) => {
          const objectiveId = `${goalId}-o${objective.responseIndex}`;
          return {
            id: objectiveId,
            objective: {
              id: objectiveId,
              description: objective.text ?? '',
            },
            interventions: objective.interventions ? [objective.interventions] : [],
          };
        });
      return {
        id: goalId,
        description: goal.text ?? '',
        ...(objectiveBlocks.length > 0 ? { objectiveBlocks } : {}),
      };
    });
}

function diagnosisSections(
  parsed: ParsedTreatmentPlanDraft,
  idPrefix: string,
): TreatmentPlanDiagnosisSection[] {
  return parsed.diagnoses.map((diagnosis) => {
    const split = splitDiagnosis(diagnosis.code);
    const symptoms = diagnosis.behavioralDefinitions
      .map((definition) => definition.text)
      .filter((text): text is string => Boolean(text));
    return {
      diagnosis: diagnosis.code,
      ...split,
      behavioralDefinitions: symptoms.length ? symptoms : undefined,
      goals: treatmentGoals(diagnosis, idPrefix),
    };
  });
}

/**
 * Extract treatment plan fields from a Diagnosis & Treatment Plan chart document response.
 *
 * Uses the repeat-aware treatment-plan parser for the nested template structure
 * (diagnosis → goals → objectives), with a flat linkId-suffix fallback for older
 * documents whose goals are not nested under a diagnosis group. No synthetic goal
 * dates or statuses are invented — they exist only when a real source exists.
 */
export function treatmentPlanFromQuestionnaireResponse(
  qr: QuestionnaireResponse,
  base: TreatmentPlan,
): TreatmentPlan {
  const diagnoses = extractDiagnosesFromQuestionnaireResponse(qr);
  const primary = diagnoses[0];
  const diagnosisCode = primary?.code;
  const diagnosisDescription = primary?.display;
  const behavioralDefinitions = collectAnswerStringsByLinkIdSuffix(qr.item, 'behavioral-definition-text');
  const parsed = parseTreatmentPlanDraft(qr.item);
  const sections = diagnosisSections(parsed, 'qr');
  const presentingProblem =
    parsed.presentingProblem ??
    findFirstAnswerByLinkIdSuffix(qr.item, 'presenting-problem') ??
    findFirstAnswerByLinkIdSuffix(qr.item, 'presenting-problem-text');
  let goals = sections.flatMap((section) => section.goals);

  if (goals.length === 0) {
    // Older/flat documents: goal items not nested under a diagnosis group.
    goals = collectAnswerStringsByLinkIdSuffix(qr.item, 'goal-text').map((description, index) => ({
      id: `qr-goal-${index}`,
      description,
    }));
  }

  const diagnosis = diagnosisCode
    ? [diagnosisCode, diagnosisDescription].filter(Boolean).join(' - ')
    : undefined;

  return {
    ...base,
    diagnosis,
    diagnosisCode: diagnosisCode || undefined,
    diagnosisDescription,
    treatmentFrequency: parsed.treatmentFrequency,
    assignedAt: parsed.assignedAt,
    expiresAt: parsed.expiresAt,
    presentingProblem,
    behavioralDefinitions: behavioralDefinitions.length > 0 ? behavioralDefinitions : undefined,
    diagnosisSections: sections.length ? sections : undefined,
    goals,
    status: qr.status === 'stopped' ? 'completed' : base.status,
  };
}

/** Display path for a signed/current plan stored as CarePlan + Goals. */
export function treatmentPlanFromCarePlan(
  carePlan: CarePlan,
  goals: Goal[],
  conditions: Condition[],
  base: TreatmentPlan,
): TreatmentPlan {
  const parsed = parseTreatmentPlanFromCarePlan(carePlan, goals, conditions);
  const sections = diagnosisSections(parsed, 'cp');
  const primary = parsed.diagnoses[0];
  const { diagnosisCode, diagnosisDescription } = splitDiagnosis(primary?.code);
  const behavioralDefinitions = parsed.diagnoses.flatMap((d) =>
    d.behavioralDefinitions.map((b) => b.text).filter((t): t is string => Boolean(t)),
  );
  const goalsMapped = sections.flatMap((section) => section.goals);

  return {
    ...base,
    diagnosis: primary?.code,
    diagnosisCode: diagnosisCode || undefined,
    diagnosisDescription,
    treatmentFrequency: parsed.treatmentFrequency,
    assignedAt: parsed.assignedAt,
    expiresAt: parsed.expiresAt,
    presentingProblem: parsed.presentingProblem,
    behavioralDefinitions: behavioralDefinitions.length > 0 ? behavioralDefinitions : undefined,
    diagnosisSections: sections.length ? sections : undefined,
    goals: goalsMapped,
    status: carePlan.status === 'active' ? 'active' : carePlan.status === 'completed' ? 'completed' : base.status,
  };
}
